import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, CreatorName, Empty, Loading } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { MessageSquare, Send, ArrowLeft, ImagePlus, Mic, Square, X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const URL_RE = /(https?:\/\/[^\s]+)/g;
function Linkify({ text }) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  return parts.map((p, i) => URL_RE.test(p)
    ? <a key={i} href={p} target="_blank" rel="noreferrer" className="underline text-eblue break-all">{p}</a>
    : <span key={i}>{p}</span>);
}

function Bubble({ m, mine }) {
  const u = mediaUrl(m.media_url);
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] rounded-2xl overflow-hidden ${mine ? "btn-blue !text-black" : "surface-2"}`}>
        {m.media_type === "image" || m.media_type === "gif" ? <img src={u} alt="" className="max-h-72 w-full object-cover" />
          : m.media_type === "video" ? <video src={u} controls className="max-h-72 w-full" />
          : m.media_type === "audio" ? <audio src={u} controls preload="metadata" className="w-56 my-1 mx-1" />
          : null}
        {m.text && <div className="px-4 py-2 text-sm whitespace-pre-wrap break-words"><Linkify text={m.text} /></div>}
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const loc = useLocation();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const end = useRef(); const fileRef = useRef(); const recRef = useRef(); const chunks = useRef([]);

  const loadConvs = () => api.get("/conversations").then(({ data }) => { setConvs(data); if (loc.state?.conv) { const c = data.find((x) => x.id === loc.state.conv); if (c) setActive(c); } });
  useEffect(() => { loadConvs(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/conversations/${active.id}/messages`).then(({ data }) => setMessages(data));
    load(); const i = setInterval(load, 5000); return () => clearInterval(i);
  }, [active]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const onMsg = (e) => { if (active && e.detail?.conversation_id === active.id) setMessages((m) => [...m, e.detail]); loadConvs(); };
    window.addEventListener("vs:message", onMsg);
    return () => window.removeEventListener("vs:message", onMsg);
  }, [active]); // eslint-disable-line

  const push = async (payload) => {
    const { data } = await api.post("/messages", { conversation_id: active.id, ...payload });
    setMessages((m) => [...m, data]); loadConvs();
  };
  const send = async () => { if (!text.trim()) return; const t = text; setText(""); try { await push({ text: t }); } catch (e) { toast.error(errText(e)); setText(t); } };

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mt = file.type.startsWith("video") ? "video" : file.type === "image/gif" ? "gif" : file.type.startsWith("audio") ? "audio" : "image";
      await push({ media_url: data.url, media_type: mt });
    } catch (e) { toast.error(errText(e)); } finally { setUploading(false); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mime type the current browser can actually record AND play back.
      const cands = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
      const mime = cands.find((c) => window.MediaRecorder?.isTypeSupported?.(c)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = mr; chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const type = (mr.mimeType || mime || "audio/webm").split(";")[0];
        const blob = new Blob(chunks.current, { type });
        if (!blob.size) { toast.error("Recording was empty — try again"); return; }
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
        await onFile(file);
      };
      mr.start();
      setRecording(true);
    } catch { toast.error("Microphone permission denied"); }
  };

  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-bold text-2xl">Messages</h1>
        <Button onClick={() => setNewOpen(true)} className="btn-blue rounded-full h-10 px-5" data-testid="new-message-btn"><Plus size={16} className="mr-1" /> New Message</Button>
      </div>
      <div className="grid md:grid-cols-[320px_1fr] gap-4 surface rounded-2xl overflow-hidden h-[72vh]">
        <div className={`border-r border-white/8 overflow-y-auto ${active ? "hidden md:block" : ""}`}>
          {convs.length === 0 ? <div className="p-6"><Empty icon={MessageSquare} title="No conversations" hint="You can message people who follow you back." /></div> :
            convs.map((c) => (
              <button key={c.id} onClick={() => setActive(c)} data-testid={`conv-${c.id}`} className={`w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors ${active?.id === c.id ? "bg-white/5" : ""}`}>
                <Avatar src={c.other?.avatar_url} name={c.other?.display_name} size={44} />
                <div className="min-w-0 flex-1 text-left"><div className="text-sm font-semibold truncate">{c.other?.display_name}</div><div className="text-xs text-muted-foreground truncate">{c.last_message || "Start chatting"}</div></div>
              </button>
            ))}
        </div>
        <div className={`flex flex-col ${!active ? "hidden md:flex" : ""}`}>
          {!active ? <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div> : (
            <>
              <div className="p-3 border-b border-white/8 flex items-center gap-3">
                <button onClick={() => setActive(null)} className="md:hidden"><ArrowLeft size={18} /></button>
                <Avatar src={active.other?.avatar_url} name={active.other?.display_name} size={36} />
                <div className="font-semibold text-sm"><CreatorName user={active.other} /></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 hide-scroll">
                {messages.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id} />)}
                <div ref={end} />
              </div>
              <div className="p-3 border-t border-white/8 flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*,video/*,image/gif" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-10 h-10 rounded-full surface-2 flex items-center justify-center text-muted-foreground hover:text-eblue shrink-0" data-testid="attach-btn">{uploading ? <Loader2 size={17} className="animate-spin" /> : <ImagePlus size={18} />}</button>
                <button onClick={toggleRecord} className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${recording ? "bg-destructive text-white" : "surface-2 text-muted-foreground hover:text-eblue"}`} data-testid="voice-btn">{recording ? <Square size={16} /> : <Mic size={18} />}</button>
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={recording ? "Recording… tap ■ to send" : "Message…"} className="flex-1 surface-2 border border-white/10 rounded-full px-4 h-10 text-sm outline-none" data-testid="message-input" />
                <button onClick={send} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0" data-testid="message-send"><Send size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>
      <NewMessageDialog open={newOpen} onClose={() => setNewOpen(false)} onStart={(conv) => { setNewOpen(false); loadConvs(); setActive(conv); }} />
    </div>
  );
}

function NewMessageDialog({ open, onClose, onStart }) {
  const [users, setUsers] = useState(null);
  useEffect(() => { if (open) api.get("/messages/eligible").then(({ data }) => setUsers(data)); }, [open]);
  const start = async (u) => { try { const { data } = await api.post("/conversations", { user_id: u.id }); onStart(data); } catch (e) { toast.error(errText(e)); } };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-sm" data-testid="new-message-dialog">
        <DialogHeader><DialogTitle className="font-display">New Message</DialogTitle><DialogDescription className="text-xs">You can message creators who follow you back.</DialogDescription></DialogHeader>
        {!users ? <Loading /> : users.length === 0 ? <Empty icon={MessageSquare} title="No mutual follows yet" hint="Follow creators who follow you to start a chat." /> : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {users.map((u) => <button key={u.id} onClick={() => start(u)} data-testid={`eligible-${u.username}`} className="w-full flex items-center gap-3 surface-2 rounded-xl p-2.5 hover:border-eblue/40 border border-white/10 text-left"><Avatar src={u.avatar_url} name={u.display_name} size={38} /><div className="min-w-0"><div className="text-sm font-semibold truncate"><CreatorName user={u} /></div><div className="text-xs text-muted-foreground">@{u.username}</div></div></button>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
